import React from 'react';

/*
 * Decorative background for the light/airy hero banners: a soft green wash with
 * gentle beams of sunlight raking down-and-left across the section (as if the sun
 * sits just past the upper-right corner). The whole fan sways slowly and the glow
 * shimmers, so it reads like moving light, with faint dust motes drifting through.
 * Purely visual — sits behind the content with -z-10 and is aria-hidden.
 *
 * Tuned for LIGHT backgrounds: beams use a `multiply`-ish soft green so they read
 * as warm shafts of light on a pale surface rather than glows on black.
 *
 * `seed` slightly varies the fan so repeated heroes don't look stamped.
 */

// Light source just past the upper-right corner of the 100x100 viewBox.
const SOURCE = { x: 96, y: -2 };

// Beams all terminate along the bottom edge, fanning left of the source so the
// light travels in a single down-and-left direction (no second spray).
const BEAMS = [
	{ x: -10, w: 9 },
	{ x: 6, w: 7 },
	{ x: 20, w: 12 },
	{ x: 34, w: 6 },
	{ x: 48, w: 10 },
	{ x: 62, w: 7 },
	{ x: 76, w: 5 },
];

// Dust motes drifting through the lit area. Hand-placed (skewed toward the
// brighter upper-right) with per-particle size, float distance, and timing so
// they don't pulse in unison.
const DUST = [
	{ x: 84, y: 18, r: 0.55, dx: -6, dy: 7, dur: 17, delay: 0 },
	{ x: 72, y: 30, r: 0.4, dx: -5, dy: 8, dur: 21, delay: 3 },
	{ x: 63, y: 22, r: 0.7, dx: -7, dy: 6, dur: 19, delay: 6 },
	{ x: 78, y: 44, r: 0.35, dx: -4, dy: 9, dur: 24, delay: 2 },
	{ x: 55, y: 38, r: 0.5, dx: -6, dy: 7, dur: 16, delay: 8 },
	{ x: 68, y: 55, r: 0.45, dx: -5, dy: 8, dur: 22, delay: 5 },
	{ x: 46, y: 28, r: 0.32, dx: -5, dy: 6, dur: 20, delay: 11 },
	{ x: 88, y: 36, r: 0.6, dx: -7, dy: 9, dur: 18, delay: 9 },
	{ x: 60, y: 66, r: 0.38, dx: -4, dy: 7, dur: 26, delay: 1 },
	{ x: 50, y: 52, r: 0.5, dx: -6, dy: 8, dur: 23, delay: 7 },
	{ x: 80, y: 62, r: 0.42, dx: -5, dy: 6, dur: 15, delay: 13 },
	{ x: 40, y: 44, r: 0.3, dx: -4, dy: 7, dur: 25, delay: 4 },
];

export default function SunRaysHero({ seed = 0 }) {
	const gid = `ray-fade-${seed}`;
	// Sway slightly faster/slower per hero so they drift out of sync.
	const swayDur = 11 + (seed % 3) * 2.5;

	return (
		<div className='absolute inset-0 -z-10 overflow-hidden' aria-hidden='true'>
			{/* Soft green wash over the light surface. */}
			<div
				className='absolute inset-0'
				style={{
					backgroundImage:
						'linear-gradient(135deg, #f3faf3 0%, #e6f4e7 55%, #d6eed8 100%)',
				}}
			/>
			{/* Warm sun glow near the source. */}
			<div
				className='sunray-glow absolute inset-0'
				style={{
					backgroundImage:
						'radial-gradient(circle at 96% -2%, rgba(255,240,190,0.85) 0%, rgba(255,240,190,0) 42%)',
				}}
			/>

			{/* Light beams */}
			<svg
				className='absolute inset-0 h-full w-full'
				viewBox='0 0 100 100'
				preserveAspectRatio='none'
			>
				<defs>
					{/* Fade runs along the beam axis (source -> bottom-left). */}
					<linearGradient
						id={gid}
						gradientUnits='userSpaceOnUse'
						x1={SOURCE.x}
						y1={SOURCE.y}
						x2='30'
						y2='100'
					>
						<stop offset='0%' stopColor='#fff7da' stopOpacity='0.85' />
						<stop offset='45%' stopColor='#bfe6c1' stopOpacity='0.5' />
						<stop offset='100%' stopColor='#9ed7a1' stopOpacity='0' />
					</linearGradient>
					{/* Soft blur so the beams read as hazy shafts, not crisp wedges. */}
					<filter
						id={`ray-blur-${seed}`}
						x='-20%'
						y='-20%'
						width='140%'
						height='140%'
					>
						<feGaussianBlur stdDeviation='1.4' />
					</filter>
				</defs>

				{/* The whole fan sways gently around the light source. */}
				<g
					className='sunray-fan'
					filter={`url(#ray-blur-${seed})`}
					style={{
						transformOrigin: `${SOURCE.x}px ${SOURCE.y}px`,
						mixBlendMode: 'soft-light',
					}}
				>
					{BEAMS.map((beam, i) => {
						// Nudge each beam by seed so heroes differ subtly.
						const drift = ((seed * 3 + i) % 5) - 2;
						const x = beam.x + drift;
						return (
							<polygon
								key={i}
								points={`${SOURCE.x},${SOURCE.y} ${x - beam.w / 2},100 ${
									x + beam.w / 2
								},100`}
								fill={`url(#${gid})`}
							/>
						);
					})}
				</g>
			</svg>

			{/* Dust motes catching the light — own SVG so circles stay round
			    (the beams layer is intentionally stretched, which would flatten
			    them). xMidYMid + a wide viewBox spreads them across the banner. */}
			<svg
				className='absolute inset-0 h-full w-full'
				viewBox='0 0 300 100'
				preserveAspectRatio='xMidYMid slice'
			>
				<defs>
					<filter
						id={`dust-blur-${seed}`}
						x='-50%'
						y='-50%'
						width='200%'
						height='200%'
					>
						<feGaussianBlur stdDeviation='0.5' />
					</filter>
				</defs>
				<g
					filter={`url(#dust-blur-${seed})`}
					style={{ mixBlendMode: 'overlay' }}
				>
					{DUST.map((d, i) => (
						<circle
							key={i}
							className='sunray-dust'
							cx={d.x * 3}
							cy={d.y}
							r={d.r}
							fill='#fff4cf'
							style={{
								'--dx': `${d.dx}px`,
								'--dy': `${d.dy}px`,
								animationDuration: `${d.dur}s`,
								animationDelay: `${d.delay}s`,
							}}
						/>
					))}
				</g>
			</svg>

			<style>{`
				@keyframes sunray-sway {
					0%   { transform: rotate(-1.6deg); }
					50%  { transform: rotate(1.6deg); }
					100% { transform: rotate(-1.6deg); }
				}
				@keyframes sunray-shimmer {
					0%, 100% { opacity: 0.7; }
					50%      { opacity: 1; }
				}
				@keyframes sunray-drift {
					0%   { transform: translate(0, 0); opacity: 0; }
					15%  { opacity: 0.8; }
					85%  { opacity: 0.8; }
					100% { transform: translate(var(--dx), var(--dy)); opacity: 0; }
				}
				.sunray-fan {
					animation: sunray-sway ${swayDur}s ease-in-out infinite;
				}
				.sunray-glow {
					animation: sunray-shimmer ${swayDur * 0.8}s ease-in-out infinite;
				}
				.sunray-dust {
					animation-name: sunray-drift;
					animation-timing-function: ease-in-out;
					animation-iteration-count: infinite;
				}
				@media (prefers-reduced-motion: reduce) {
					.sunray-fan, .sunray-glow, .sunray-dust { animation: none; }
				}
			`}</style>
		</div>
	);
}
